import { Box, Stack, styled, Typography } from '@mui/material';
import useActiveOffice from 'new/hooks/useActiveOffice';
import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import LinkButton from 'new/components/LinkButton';
import { TENANT } from 'new/tenants';

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  marginBottom: theme.spacing(4),
}));

const Paragraph = styled(Typography)(() => ({
  fontSize: '0.875rem',
}));

const InputBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  width: theme.spacing(30),
  borderTop: '1px solid black',
  fontSize: '0.875rem',
}));

const printStyles = `
  @media print {
    body {
      padding: 10mm 20mm; 
      -webkit-print-color-adjust: exact; 
    };
    @page {
      size: A4 portrait 
    };
  };
`;

type Props = {
  label: string;
  CustomButton?: React.ComponentType<unknown>;
};

const MembershipAgreement: React.FC<Props> = ({ label, CustomButton }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const { user } = useActiveOffice();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });
  const Button = CustomButton ? CustomButton : LinkButton;

  return (
    <>
      <Button onClick={handlePrint} asLink>
        {label}
      </Button>
      <Box sx={{ display: 'none' }}>
        <Stack ref={printRef}>
          <style>{printStyles}</style>
          <Stack alignItems="center">
            {!!user?.data.logo && (
              <img
                width="200px"
                alt={`${user?.data.officeName} office logo`}
                src={user?.data.logo}
              />
            )}
            <Title variant="h4" style={{ textAlign: 'center' }}>
              {user?.data.officeName}
              <br />
              {TENANT.nameSimple} Membership Plan In-Office Agreement
            </Title>
          </Stack>
          <Stack gap={1.5}>
            <Paragraph>
              The {TENANT.nameSimple} Membership Plan is{' '}
              <b>not {TENANT.nameSimple.toLowerCase()} insurance</b>; it’s a{' '}
              <b>loyalty program</b> between you (and any individuals you enroll
              under your Account, each a “Plan Participant”) and our practice.
            </Paragraph>
            <Paragraph>
              Each Plan Participant is enrolled for a <b>one-year term</b>{' '}
              starting on their enrollment date (the “Initial Term”), which{' '}
              <b>automatically renews annually</b> (“Renewal Term”) unless you
              disable auto-renew before the current term ends.
            </Paragraph>
            <Paragraph>
              You may cancel your membership at any time by contacting our
              office, or by logging into your account and turning off
              auto-renewal.
            </Paragraph>
            <Paragraph>
              By enrolling and making required payments, you and each Plan
              Participant become eligible for the <b>Included Services</b>. As
              long as Membership Fees are current, there are no additional
              charges for those services. You are still responsible for
              Non-Included Services and any applicable taxes, per our current
              payment policies.
            </Paragraph>
            <Paragraph>
              You agree not to <b>reverse or chargeback</b> any fees. If you do,
              or stop payment during the annual term, we may terminate your and
              all associated enrollments. You must reimburse us for any related
              costs, including chargeback fees, unpaid balances, and time spent
              resolving the issue. We may also refer your account to a debt
              collection agency if payment isn’t received on time.
            </Paragraph>
            <Paragraph mt={6} mb={4}>
              By signing below, you are agreeing to the full {TENANT.nameSimple}{' '}
              Membership Plan Terms & Conditions, which can be found at{' '}
              <strong>{TENANT.prodUrl}/terms </strong>.
            </Paragraph>
            <InputBox>Print Name</InputBox>
            <InputBox>Signature</InputBox>
            <InputBox>Date</InputBox>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default MembershipAgreement;
